import { createDeviceKey } from "../crypto";
import { fetchTrustStore } from "../issuer-keys";
import {
  saveClockOffset, saveCredential, saveCredentialLite, saveDeviceKey, saveTrustStore,
} from "../storage";
import { type LogoutOutcome, uploadReceipts } from "../sync";

const b64 = (raw: Uint8Array) =>
  btoa(String.fromCharCode(...raw)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const unb64 = (text: string) =>
  Uint8Array.from(atob(text.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));

/**
 * Why a holder who already had a credential is back at this form, by the
 * issuer's refusal. `revoked` covers the two ways another device causes it:
 * an enrolment there with «Desactivar mis otros dispositivos» ticked, and one
 * that took the person past the issuer's limit of devices.
 */
const RE_ENROL_COPY: Record<string, string> = {
  "revoked":
    "Tu identidad en este dispositivo ya no es válida: la revocó la UH, o quedó desactivada cuando descargaste tu identidad en otro dispositivo. Descárgala de nuevo con tu usuario y contraseña.",
  "left-directory":
    "Tu identidad ya no es válida porque no apareces en el directorio de la UH. Si crees que es un error, consúltalo con tu facultad.",
  "expired-too-long":
    "Tu identidad lleva más de treinta días caducada. Descárgala de nuevo con tu usuario y contraseña.",
};

/**
 * What the form says after a logout. The second one is the case the holder
 * has to understand: the issuer never heard, so the credential is still on
 * no status list, and what makes it harmless is that its key is gone.
 */
const LOGOUT_COPY: Record<LogoutOutcome, string> = {
  "revoked":
    "Cerraste sesión en este dispositivo y la UH confirmó que la identidad que tenía ya no es válida. Para volver a usarlo, descarga tu identidad con tu usuario y contraseña.",
  "unrevoked":
    "Cerraste sesión en este dispositivo, pero no se pudo avisar a la UH. La identidad que tenía sigue sin revocar hasta que caduque, en siete días como mucho, pero nadie puede presentarla, porque presentarla exige la llave que se acaba de borrar de aquí.",
};

/** Why the form is on screen when it is not a first enrolment: the issuer's
 *  refusal when the credential on this device died, or how a logout went. */
export type EnrolReason = { dead: string } | { loggedOut: LogoutOutcome };

function noticeHTML(reason: EnrolReason | undefined): string {
  if (reason === undefined) return "";
  if ("dead" in reason) {
    return `<p id="notice" class="warn">${RE_ENROL_COPY[reason.dead] ?? RE_ENROL_COPY["revoked"]}</p>`;
  }
  const warn = reason.loggedOut === "unrevoked" ? ` class="warn"` : "";
  return `<p id="notice"${warn}>${LOGOUT_COPY[reason.loggedOut]}</p>`;
}

/**
 * The body of `POST /api/alta`. A ticked checkbox is the string "on" in a
 * form and an unticked one is absent, and the issuer reads a boolean.
 */
export function enrolmentBody(data: FormData, devicePublicKey: string) {
  return {
    username: data.get("username"),
    password: data.get("password"),
    device_public_key: devicePublicKey,
    revoke_others: data.get("revoke_others") !== null,
  };
}

export function renderEnrol(root: HTMLElement, onDone: () => void, reason?: EnrolReason): void {
  root.innerHTML = `
    <div class="uhb">Universidad de La Habana</div>
    <h1 class="title">Identidad UH</h1>${noticeHTML(reason)}
    <form id="enrol">
      <label>Usuario <input name="username" autocomplete="username" required></label>
      <label>Contraseña <input name="password" type="password" autocomplete="current-password" required></label>
      <label class="check"><input name="revoke_others" type="checkbox"> Desactivar mis otros dispositivos</label>
      <p class="hint">Márcala si perdiste un teléfono o ya no usas otro dispositivo donde
      descargaste tu identidad. Si no la marcas, tus otros dispositivos siguen funcionando.</p>
      <button type="submit">Descargar mi identidad</button>
    </form>
    <p id="error" role="alert"></p>`;

  const form = root.querySelector<HTMLFormElement>("#enrol")!;
  const error = root.querySelector<HTMLParagraphElement>("#error")!;

  const submit = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    // Busy while the issuer answers, so a second tap does not mint a second
    // device key. Whatever the outcome, the button comes back: on success
    // `onDone` has already drawn the next screen over it.
    submit.disabled = true;
    submit.textContent = "Descargando…";
    void enrol().finally(() => {
      submit.disabled = false;
      submit.textContent = "Descargar mi identidad";
    });
  });

  const enrol = async () => {
    error.textContent = "";
    const data = new FormData(form);
    const pair = await createDeviceKey();
    const devicePublicKey = b64(
      new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey)),
    );
    // Before `/api/alta`, and before anything below replaces the device key:
    // the issuer checks each receipt against the key of the credential it is
    // uploaded with, so a receipt still queued once this form finishes is one
    // it will reject. Whatever does not go now is lost, and says so as
    // `rejected` at the next sync; it must not hold up the enrolment.
    await uploadReceipts().catch(() => undefined);
    // The one moment this application needs the network, and the one place a
    // rejected `fetch` can happen. Uncaught it leaves the form looking as
    // though the button did nothing.
    let response: Response;
    try {
      response = await fetch("/api/alta", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(enrolmentBody(data, devicePublicKey)),
      });
    } catch {
      error.textContent = "No se pudo conectar con la UH. Revisa la red.";
      return;
    }
    if (!response.ok) {
      error.textContent =
        response.status === 401
          ? "Usuario o contraseña incorrectos"
          : "No se pudo completar el alta";
      return;
    }
    // The keys every later scan is verified against, fetched before anything
    // is stored. A wallet holding a credential and no trust store shows its
    // own card and refuses every code it scans, so it is not enrolled: better
    // to say so now, while the holder still has the network, than in a
    // corridor without it.
    let trustStore: Map<number, Uint8Array>;
    try {
      trustStore = await fetchTrustStore();
    } catch {
      error.textContent =
        "No se pudo obtener la llave de la UH. Revisa la red e inténtalo de nuevo.";
      return;
    }
    // The only moment this application ever learns what time it is. Everything
    // downstream reads `correctedNow`, and a device that skipped this line
    // refuses to verify at all rather than trusting a clock nobody set.
    const serverSeconds = Date.parse(response.headers.get("date") ?? "") / 1000;
    if (!Number.isNaN(serverSeconds)) {
      await saveClockOffset(Math.round(serverSeconds - Date.now() / 1000));
    }
    await saveTrustStore(trustStore);
    await saveDeviceKey(pair);
    const body = (await response.json()) as { credential: string; credential_lite?: unknown };
    await saveCredential(unb64(body.credential));
    // Written even when the issuer sent none: a lite credential left from an
    // earlier enrolment is bound to a device key this form just replaced.
    await saveCredentialLite(
      typeof body.credential_lite === "string" ? unb64(body.credential_lite) : null,
    );
    onDone();
  };
}
